'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  Rocket, 
  ChevronUp, 
  Check, 
  Copy, 
  ExternalLink, 
  TrendingUp, 
  Circle, 
  ArrowRight,
  Smartphone,
  X,
  CheckCircle2
} from 'lucide-react';

export default function DashboardOverviewPage() {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [salesSummary, setSalesSummary] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(true);
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [installed, setInstalled] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState<{ connected: boolean; phone?: string | null }>({ connected: false });
  const [mpConnected, setMpConnected] = useState(false);
  const [showWaConnectedModal, setShowWaConnectedModal] = useState(false);

  useEffect(() => {
    // Escuchar evento de instalación PWA nativo
    const handleBeforeInstall = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Cargar métricas en vivo desde PostgreSQL Prisma e integraciones
    async function loadDashboardData() {
      try {
        const [resB, resC, resS, resWa, resPm] = await Promise.all([
          fetch('/api/bookings'),
          fetch('/api/clients'),
          fetch('/api/sales'),
          fetch('/api/whatsapp/instance'),
          fetch('/api/payment-methods')
        ]);
        const [dataB, dataC, dataS, dataWa, dataPm] = await Promise.all([
          resB.json(),
          resC.json(),
          resS.json(),
          resWa.json().catch(() => ({})),
          resPm.json().catch(() => ({}))
        ]);
        if (dataB.success) setAppointments(dataB.appointments || []);
        if (dataC.success) setClients(dataC.clients || []);
        if (dataS.success) setSalesSummary(dataS);

        if (dataWa?.success && dataWa?.instance) {
          const isConn = Boolean(dataWa.instance.connected || dataWa.instance.status === 'CONNECTED');
          setWhatsappStatus({
            connected: isConn,
            phone: dataWa.instance.phone
          });
        }

        if (dataPm?.success && dataPm?.mercadopago) {
          setMpConnected(Boolean(dataPm.mercadopago.connected && dataPm.mercadopago.accessToken));
        }
      } catch (e) {
        console.error('Error cargando métricas:', e);
      }
    }
    loadDashboardData();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const [publicUrlText, setPublicUrlText] = useState('localhost:3009');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPublicUrlText(window.location.host);
    }
  }, []);

  const handleCopyLink = () => {
    const url = window.location.origin;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      setInstallModalOpen(true);
    }
  };

  const totalRevenue = salesSummary ? salesSummary.grandTotal : 0;
  const onlineBookingsCount = appointments.filter(a => a.bookedOnline !== false).length;
  const onlinePercentage = appointments.length > 0 
    ? Math.round((onlineBookingsCount / appointments.length) * 100) 
    : 100;

  return (
    <div className="space-y-4 sm:space-y-5 max-w-5xl mx-auto">
      {/* 1. HERO CARD JUAN ROJAS (Teal #004D44 con barra de 14 días y banner 40% OFF) */}
      <div className="bg-gradient-to-r from-[#003630] via-[#004D44] to-[#006E63] text-white rounded-3xl p-6 sm:p-7 shadow-sm relative overflow-hidden">
        {/* Fila superior: Avatar y Nombre */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-[#006E63] flex items-center justify-center font-black text-[#A3E3DC] text-lg">
              JR
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight text-white leading-tight">Juan Rojas</h2>
              <p className="text-xs text-[#A3E3DC] font-medium">Hola, Juan 👋</p>
            </div>
          </div>
        </div>

        {/* Barra de 14 días de prueba gratis */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-[#A3E3DC] mb-2 font-medium">
            <span>Prueba gratis · quedan 14 días</span>
          </div>
          {/* Barra segmentada en 14 partes exactas */}
          <div className="grid grid-cols-14 gap-1 sm:gap-1.5 h-1.5 w-full">
            {Array.from({ length: 14 }).map((_, i) => (
              <div key={i} className="bg-[#00A896] rounded-full h-full"></div>
            ))}
          </div>
        </div>

        {/* Banner 40% OFF en tu primer mes */}
        <div className="mt-5 bg-[#003630] border border-[#00A896]/30 rounded-2xl p-3.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <div className="text-xs sm:text-sm font-bold text-amber-300 flex items-center gap-1.5">
              40% OFF en tu primer mes
            </div>
            <div className="text-xs text-teal-100/80 mt-0.5">
              <strong>$14.500</strong> · hoy no pagás nada
            </div>
          </div>

          <button className="px-5 py-2 bg-[#f59e0b] hover:bg-amber-500 text-slate-950 font-extrabold text-xs rounded-xl shadow-md transition-all self-end sm:self-auto cursor-pointer">
            Activar
          </button>
        </div>
      </div>

      {/* 2. BARRA DE ENLACE PÚBLICO (Enlace real a nuestra app local + Copiar) */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2.5">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white hover:bg-[#F0FAF9] border border-slate-200/80 rounded-2xl px-4 py-3 flex items-center justify-between text-xs font-semibold text-slate-800 transition shadow-sm group"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00A896]"></span>
            <span className="font-mono text-slate-700">{publicUrlText}/ (Portal de Reservas)</span>
          </div>
          <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#00A896] transition" />
        </a>

        <button
          onClick={handleCopyLink}
          className="bg-white hover:bg-[#F0FAF9] border border-slate-200/80 rounded-2xl px-5 py-3 flex items-center justify-center gap-2 text-xs font-bold text-slate-800 transition shadow-sm cursor-pointer"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-[#00A896]" />
              <span className="text-[#005C52]">¡Copiado!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 text-slate-500" />
              <span>Copiar</span>
            </>
          )}
        </button>
      </div>

      {/* 3. TARJETA DE INGRESOS DEL MES */}
      <div className="bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-7 shadow-sm">
        <div className="text-xs font-bold uppercase tracking-wider text-[#00A896] flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-[#00A896]" />
          INGRESOS DEL MES
        </div>
        <div className="text-5xl sm:text-6xl font-black text-slate-900 tracking-tight mt-3">
          ${totalRevenue.toLocaleString()}
        </div>
      </div>

      {/* 4. LAS 4 TARJETAS DE KPIS EN BLANCO */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5 sm:gap-4">
        {/* Turnos del mes */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm flex flex-col justify-between h-28">
          <span className="text-xs text-slate-500 font-medium">Turnos del mes</span>
          <div className="text-3xl font-black text-slate-900">
            {appointments.length}
          </div>
          <span className="text-[10px] text-[#00A896] font-bold block">
            {appointments.filter(a => a.status === 'confirmed').length} activos
          </span>
        </div>

        {/* Clientes */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm flex flex-col justify-between h-28">
          <span className="text-xs text-slate-500 font-medium">Clientes</span>
          <div className="text-3xl font-black text-slate-900">
            {clients.length}
          </div>
          <span className="text-[10px] text-[#00A896] font-bold block">
            +{clients.length} este mes
          </span>
        </div>

        {/* Visitas a tu web (Auditoría: 0 / Tracking local) */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm flex flex-col justify-between h-28">
          <span className="text-xs text-slate-500 font-medium">Visitas a tu web</span>
          <div className="text-3xl font-black text-slate-900">
            0
          </div>
          <span className="text-[10px] text-slate-400 font-medium block">
            Sin analítica externa
          </span>
        </div>

        {/* Reservaron solos */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm flex flex-col justify-between h-28">
          <span className="text-xs text-slate-500 font-medium">Reservaron solos</span>
          <div className="text-3xl font-black text-slate-900">
            {onlineBookingsCount}
          </div>
          <span className="text-[10px] text-[#00A896] font-bold block">
            {onlinePercentage}% del mes
          </span>
        </div>
      </div>

      {/* 5. CHECKLIST 'TERMINÁ DE CONECTAR TU NEGOCIO' (Dinámico y reactivo) */}
      <div className="bg-white border-2 border-[#00A896]/20 rounded-3xl p-6 shadow-sm space-y-4">
        {/* Encabezado colapsable */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#E6F6F4] text-[#00A896] flex items-center justify-center">
              <Rocket className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Terminá de conectar tu negocio
              </h3>
              <p className="text-xs text-[#005C52] font-medium">
                {(whatsappStatus.connected ? 1 : 0) + (mpConnected ? 1 : 0) + (installed ? 1 : 0)} de 3 listos
              </p>
            </div>
          </div>

          <button
            onClick={() => setChecklistOpen(!checklistOpen)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 transition cursor-pointer"
          >
            <ChevronUp className={`w-5 h-5 transition-transform duration-200 ${checklistOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Filas de acciones */}
        {checklistOpen && (
          <div className="space-y-3 pt-1">
            {/* Fila 1: WhatsApp */}
            <div className="flex items-center justify-between p-2 hover:bg-[#F0FAF9] rounded-2xl transition">
              <div className="flex items-center gap-3">
                {whatsappStatus.connected ? (
                  <CheckCircle2 className="w-5 h-5 text-[#00A896] fill-[#E6F6F4] shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-[#00A896] stroke-2 shrink-0" />
                )}
                <div>
                  <span className="text-sm font-bold text-slate-900 block">
                    {whatsappStatus.connected ? 'WhatsApp conectado' : 'Conectá tu WhatsApp'}
                  </span>
                  {whatsappStatus.connected && (
                    <span className="text-[11px] font-medium text-[#005C52] block">
                      Línea: {whatsappStatus.phone || 'Instancia activa'}
                    </span>
                  )}
                </div>
              </div>
              {whatsappStatus.connected ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowWaConnectedModal(true)}
                    className="px-3 py-1.5 bg-[#E6F6F4] hover:bg-[#CCF0EC] border border-[#00A896]/30 text-[#005C52] font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-2xs"
                  >
                    <Check className="w-3.5 h-3.5 text-[#00A896]" />
                    <span>Conectado</span>
                  </button>
                  <Link
                    href="/dashboard/whatsapp?tab=chats"
                    className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition"
                    title="Ver chats"
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              ) : (
                <Link
                  href="/dashboard/whatsapp"
                  className="px-4 py-2 bg-[#00875a] hover:bg-[#0a5c36] text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 shadow-sm"
                >
                  <span>Conectar</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>

            {/* Fila 2: Mercado Pago */}
            <div className="flex items-center justify-between p-2 hover:bg-slate-50/80 rounded-2xl transition">
              <div className="flex items-center gap-3">
                {mpConnected ? (
                  <CheckCircle2 className="w-5 h-5 text-sky-600 fill-sky-100 shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-sky-400 stroke-2 shrink-0" />
                )}
                <div>
                  <span className="text-sm font-bold text-slate-900 block">
                    {mpConnected ? 'Mercado Pago conectado' : 'Conectá Mercado Pago'}
                  </span>
                  {mpConnected && (
                    <span className="text-[11px] font-medium text-sky-700 block">
                      Cobro de señas activo
                    </span>
                  )}
                </div>
              </div>
              {mpConnected ? (
                <div className="flex items-center gap-2">
                  <Link
                    href="/dashboard/settings/payment-methods"
                    className="px-3 py-1.5 bg-sky-50 hover:bg-sky-100 border border-sky-200 text-sky-800 font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-2xs"
                  >
                    <Check className="w-3.5 h-3.5 text-sky-600" />
                    <span>Configurado</span>
                  </Link>
                </div>
              ) : (
                <Link
                  href="/dashboard/settings/payment-methods"
                  className="px-4 py-2 bg-[#009ee3] hover:bg-[#0089c7] text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 shadow-sm"
                >
                  <span>Conectar</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>

            {/* Fila 3: Instalar app */}
            <div className="flex items-center justify-between p-2 hover:bg-slate-50/80 rounded-2xl transition">
              <div className="flex items-center gap-3">
                {installed ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 fill-emerald-100 shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300 stroke-2 shrink-0" />
                )}
                <span className="text-sm font-bold text-slate-900">
                  {installed ? 'App instalada' : 'Instalá la app'}
                </span>
              </div>
              <button
                onClick={handleInstallClick}
                className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 font-bold text-xs rounded-xl transition flex items-center gap-1.5 shadow-sm cursor-pointer"
              >
                <span>{installed ? 'Instalada' : 'Instalar'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal WhatsApp Conectado Exitosamente */}
      {showWaConnectedModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-slate-100 relative space-y-4 animate-in zoom-in-95 duration-200">
            <button
              onClick={() => setShowWaConnectedModal(false)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7" />
            </div>

            <div>
              <h3 className="text-lg font-bold text-slate-900">
                WhatsApp Conectado
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Tu instancia de Evolution API está vinculada correctamente con Calenddy.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Línea vinculada:</span>
                <span className="font-bold text-slate-900">{whatsappStatus.phone || 'Conectado'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Estado del servicio:</span>
                <span className="font-bold text-emerald-600 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                  En línea y despachando
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Link
                href="/dashboard/whatsapp?tab=chats"
                className="flex-1 py-3 bg-[#00875a] hover:bg-[#0a5c36] text-white font-bold text-xs rounded-xl transition text-center shadow-sm"
              >
                Ver Bandeja de Chats
              </Link>
              <button
                onClick={() => setShowWaConnectedModal(false)}
                className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition cursor-pointer"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Guía de Instalación PWA (para navegadores que no lanzan prompt automático) */}
      {installModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 text-center shadow-2xl space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto">
              <Smartphone className="w-6 h-6" />
            </div>

            <h3 className="text-base font-bold text-slate-900">Instalar AppGenda</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Para tener acceso directo en tu pantalla de inicio como una app nativa:
            </p>

            <div className="bg-slate-50 rounded-2xl p-3 text-left text-xs text-slate-700 space-y-2 border border-slate-100">
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] shrink-0 font-bold">1</span>
                <span>En el menú de tu navegador toca <strong>«Compartir»</strong> o los tres puntos <strong>(⋮)</strong>.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] shrink-0 font-bold">2</span>
                <span>Selecciona <strong>«Agregar a pantalla de inicio»</strong> o <strong>«Instalar aplicación»</strong>.</span>
              </div>
            </div>

            <button
              onClick={() => setInstallModalOpen(false)}
              className="w-full py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-xl transition"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
